/* Patient — "My Aligner Case" page · iter98.12 redesign.
 *
 * Translated from the user-supplied "My Journey" HTML mockup with the
 * Apollo cyan-teal palette retained (the mockup's emerald has been
 * mapped to `hsl(var(--primary))`).
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────┐
 *   │  Phase banner — situational gradient + status pills      │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  Treatment timeline — 4 phases (Scan · Treatment ·       │
 *   │     Retention · Complete) with dots, partial-fill lines  │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  4 mini stat tiles (aligners completed · weeks active ·  │
 *   │     check-ins · avg wear)                                │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  Tab bar: Aligners · Retention · Photo Timeline ·        │
 *   │           Wear Log · Doctor Notes                        │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  Tab body                                                 │
 *   └──────────────────────────────────────────────────────────┘
 *
 * Data fetched once on mount (5 parallel calls):
 *   /cases/mine
 *   /patient/me/aligner-progress
 *   /patient/me/care-team
 *   /patient/me/check-ins
 *   /patient/me/wear-log?range=30
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    Tooth, ShieldCheck, Camera, Clock, Notebook,
    CheckCircle, CaretDown, CaretUp, ArrowRight, CalendarPlus,
    Sparkle, CalendarBlank, ChatCircleDots, Plus, ClockUser, Warning,
    XCircle,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import TreatmentLockedCard from "@/components/patient/TreatmentLockedCard";
import DoctorAvatar from "@/components/common/DoctorAvatar";

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────
const fmtDate = (iso, opts = { day: "2-digit", month: "short", year: "numeric" }) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", opts); }
    catch { return String(iso); }
};

const fmtShortDate = (iso) => fmtDate(iso, { day: "2-digit", month: "short" });

const fmtRelative = (iso) => {
    if (!iso) return "—";
    try {
        const then = new Date(iso);
        const days = Math.floor((Date.now() - then.getTime()) / 86400000);
        if (days === 0) return "today";
        if (days === 1) return "yesterday";
        if (days < 7) return `${days} days ago`;
        if (days < 30) return `${Math.floor(days / 7)} wks ago`;
        return fmtShortDate(iso);
    } catch { return ""; }
};

function derivePhase(activeCase) {
    if (!activeCase) return "no_case";
    const kind = caseKindOf(activeCase);

    if (!activeCase.treatment_activated_at) {
        if (kind === "retainer") return "retainer_in_production";
        if (kind === "refinement") return "refinement_pre_treatment";
        return "pre_treatment";
    }

    // Retainer cases — once activated, the patient is wearing their custom
    // retainer; no concept of aligner switching here.
    if (kind === "retainer") return "retainer_active";

    if (["completed", "retention"].includes(activeCase.status)) {
        return kind === "refinement" ? "refinement_complete" : "complete";
    }

    const total = Math.max(
        Number(activeCase.plan?.aligner_count_upper || 0),
        Number(activeCase.plan?.aligner_count_lower || 0),
    );
    const stage = Number(activeCase.current_aligner_stage || 0);

    if (kind === "refinement") {
        if (total > 0 && stage >= total) return "refinement_final_aligner";
        return "refinement_in_treatment";
    }

    if (total > 0 && stage >= total) return "final_aligner";
    return "in_treatment";
}

// ── case_kind helpers ──────────────────────────────────────────────
// Backend tags every case with `case_kind` ∈ {primary, refinement, retainer}.
// Treat missing as "primary" so legacy cases pre-iter-89 still render.
const caseKindOf = (c) => (c?.case_kind || "primary");

const caseKindMeta = {
    primary:    { label: "Primary case",     short: "Primary",    accent: "bg-primary/10 text-primary ring-primary/20" },
    refinement: { label: "Refinement cycle", short: "Refinement", accent: "bg-amber-50 text-amber-800 ring-amber-200" },
    retainer:   { label: "Custom retainer",  short: "Retainer",   accent: "bg-indigo-50 text-indigo-700 ring-indigo-200" },
};

const codeOf = (c) => c?.case_code || c?.code || (c?.id ? c.id.slice(0, 8) : "—");

/* Count how many cases of a given kind exist for the patient up to and
   including `target` — used to label "Refinement #1", "#2" etc. Cases are
   sorted by `created_at` ascending so the first refinement is #1. */
function cycleNumber(cases, target) {
    const kind = caseKindOf(target);
    if (kind === "primary") return 0;
    const sorted = [...(cases || [])]
        .filter((c) => caseKindOf(c) === kind)
        .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    const idx = sorted.findIndex((c) => c.id === target.id);
    return idx < 0 ? sorted.length : idx + 1;
}

// ════════════════════════════════════════════════════════════════════
// MAIN
// ════════════════════════════════════════════════════════════════════
export default function PatientMyCase() {
    const [cases, setCases] = useState([]);
    const [progress, setProgress] = useState(null);
    const [careTeam, setCareTeam] = useState(null);
    const [checkIns, setCheckIns] = useState({ check_ins: [], this_week_done: false });
    const [wearLog, setWearLog] = useState({ logs: [] });
    const [loading, setLoading] = useState(true);
    const [activeCaseId, setActiveCaseId] = useState(null);
    const [tab, setTab] = useState("aligners");

    useEffect(() => {
        Promise.all([
            api.get("/cases/mine").then((r) => r.data).catch(() => []),
            api.get("/patient/me/aligner-progress").then((r) => r.data).catch(() => null),
            api.get("/patient/me/care-team").then((r) => r.data).catch(() => null),
            api.get("/patient/me/check-ins").then((r) => r.data).catch(() => ({ check_ins: [] })),
            api.get("/patient/me/wear-log?range=30").then((r) => r.data).catch(() => ({ logs: [] })),
        ]).then(([cs, p, ct, ci, wl]) => {
            const live = (cs || []).filter((c) => c.status !== "cancelled");
            setCases(live);
            setProgress(p?.active ? p : null);
            setCareTeam(ct || null);
            setCheckIns(ci || { check_ins: [], this_week_done: false });
            setWearLog(wl || { logs: [] });
            if (live.length > 0) setActiveCaseId(live[0].id);
        }).finally(() => setLoading(false));
    }, []);

    const activeCase = useMemo(() => cases.find((c) => c.id === activeCaseId) || cases[0] || null, [cases, activeCaseId]);

    if (loading) {
        return <div className="container mx-auto px-4 py-10 text-center text-sm text-muted-foreground">Loading your journey…</div>;
    }

    if (!activeCase) {
        return (
            <div className="max-w-md mx-auto py-16 text-center" data-testid="patient-mycase-empty">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-secondary mb-4">
                    <Tooth size={26} weight="duotone" className="text-muted-foreground" />
                </div>
                <h2 className="font-display text-xl font-bold">Your aligner journey starts after your first visit</h2>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Once your doctor fits your aligners and activates your treatment, your journey — wear tracking, switch reminders &amp; weekly check-ins — will appear here.
                </p>
            </div>
        );
    }

    if (!activeCase.treatment_activated_at) {
        return (
            <div className="container mx-auto px-4 py-8 max-w-3xl" data-testid="patient-mycase-locked">
                <TreatmentLockedCard />
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6 max-w-4xl" data-testid="patient-mycase-page">
            {/* Case identity strip — ALWAYS visible; gives the patient
                the case code (needed when calling the clinic), the case
                kind chip (primary / refinement #N / retainer), the
                doctor + clinic line, and an inline link back to the
                parent case for refinement / retainer children. */}
            <CaseIdentityStrip
                activeCase={activeCase}
                cases={cases}
                onSwitchCase={setActiveCaseId}
            />

            <PhaseBanner activeCase={activeCase} progress={progress} careTeam={careTeam} cases={cases} />
            <TreatmentTimeline activeCase={activeCase} progress={progress} cases={cases} />
            <StatsRow activeCase={activeCase} progress={progress} checkIns={checkIns} wearLog={wearLog} />

            <TabBar tab={tab} setTab={setTab} />

            <div className="mt-2">
                {tab === "aligners" && <AlignersTab activeCase={activeCase} progress={progress} />}
                {tab === "retention" && <RetentionTab activeCase={activeCase} progress={progress} careTeam={careTeam} />}
                {tab === "photos" && <PhotoTimelineTab activeCase={activeCase} />}
                {tab === "wear" && <WearLogTab wearLog={wearLog} progress={progress} onReload={() => api.get("/patient/me/wear-log?range=30").then((r) => setWearLog(r.data))} />}
                {tab === "notes" && <DoctorNotesTab progress={progress} careTeam={careTeam} />}
            </div>
        </div>
    );
}


// ════════════════════════════════════════════════════════════════════
// CASE IDENTITY STRIP — always-visible case code + kind + doctor row
// ════════════════════════════════════════════════════════════════════
function CaseIdentityStrip({ activeCase, cases, onSwitchCase }) {
    const kind = caseKindOf(activeCase);
    const meta = caseKindMeta[kind] || caseKindMeta.primary;
    const cycleN = cycleNumber(cases, activeCase);
    const kindLabel = kind === "primary"
        ? meta.label
        : `${meta.label}${cycleN > 0 ? ` #${cycleN}` : ""}`;

    // Find the parent case (if this is a child) so the patient can jump
    // back to the case that spawned this refinement / retainer.
    const parent = activeCase.parent_case_id
        ? (cases || []).find((c) => c.id === activeCase.parent_case_id)
        : null;

    // Sort cases for the selector: primary first, then children by
    // created_at ascending.
    const sortedCases = useMemo(() => {
        const arr = [...(cases || [])];
        arr.sort((a, b) => {
            const ak = caseKindOf(a) === "primary" ? 0 : 1;
            const bk = caseKindOf(b) === "primary" ? 0 : 1;
            if (ak !== bk) return ak - bk;
            return (a.created_at || "").localeCompare(b.created_at || "");
        });
        return arr;
    }, [cases]);

    return (
        <div className="mb-3" data-testid="case-identity-strip">
            {/* Identity row */}
            <div className="rounded-xl border border-border bg-card px-4 py-2.5 flex flex-wrap items-center gap-2.5">
                <div className="font-mono text-xs font-bold tracking-wide text-foreground" data-testid="case-code">
                    {codeOf(activeCase)}
                </div>
                <span
                    className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ring-1 ${meta.accent}`}
                    data-testid="case-kind-chip"
                >
                    {kindLabel}
                </span>

                {parent && (
                    <button
                        onClick={() => onSwitchCase(parent.id)}
                        className="text-[11px] text-muted-foreground hover:text-primary inline-flex items-center gap-1 transition"
                        title="Switch to the parent case"
                        data-testid="parent-case-link"
                    >
                        <ArrowRight size={11} className="rotate-180" />
                        <span className="hidden sm:inline">linked to</span>
                        <span className="font-mono font-semibold">{codeOf(parent)}</span>
                    </button>
                )}

                <div className="flex-1" />

                {activeCase.doctor_name && (
                    <div className="text-[11px] text-muted-foreground truncate">
                        {activeCase.doctor_name}
                        {activeCase.clinic_name ? ` · ${activeCase.clinic_name}` : ""}
                    </div>
                )}
            </div>

            {/* Multi-case selector pills — only shown when patient has > 1 case */}
            {sortedCases.length > 1 && (
                <div className="mt-2 flex gap-1.5 flex-wrap" data-testid="case-selector">
                    {sortedCases.map((c) => {
                        const m = caseKindMeta[caseKindOf(c)] || caseKindMeta.primary;
                        const cyc = cycleNumber(sortedCases, c);
                        const label = caseKindOf(c) === "primary"
                            ? `${codeOf(c)} · ${m.short}`
                            : `${codeOf(c)} · ${m.short} #${cyc}`;
                        const isActive = c.id === activeCase.id;
                        return (
                            <button
                                key={c.id}
                                onClick={() => onSwitchCase(c.id)}
                                className={`px-2.5 h-7 rounded-full text-[11px] font-semibold border transition ${
                                    isActive
                                        ? "bg-foreground text-white border-foreground"
                                        : "border-border text-muted-foreground hover:border-primary/50"
                                }`}
                                data-testid={`case-pill-${c.id}`}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════
// PHASE BANNER — situational gradient hero
// ════════════════════════════════════════════════════════════════════
function PhaseBanner({ activeCase, progress, careTeam, cases }) {
    const phase = derivePhase(activeCase);
    const kind = caseKindOf(activeCase);
    const total = Math.max(
        Number(activeCase?.plan?.aligner_count_upper || 0),
        Number(activeCase?.plan?.aligner_count_lower || 0),
    );
    const stage = Number(activeCase?.current_aligner_stage || 0);
    const startedAt = activeCase?.treatment_activated_at;
    const cycleN = cycleNumber(cases, activeCase);
    const cycleSuffix = cycleN > 0 ? ` #${cycleN}` : "";

    const variants = {
        in_treatment: {
            kicker: "Current phase",
            title: `🦷 Treatment in progress — Aligner ${stage} of ${total || "?"}`,
            sub: "Wear your aligners 22 hours a day. Your doctor reviews your weekly check-in to make sure you're on track.",
            pills: [
                { text: `${stage}/${total || "?"} aligners` },
                ...(progress?.stage?.next_switch_in_days != null ? [{ text: `Next switch in ${progress.stage.next_switch_in_days}d` }] : []),
                ...(startedAt ? [{ text: `Started ${fmtDate(startedAt, { month: "short", year: "numeric" })}` }] : []),
                { text: "🟢 On Track" },
            ],
            ctaLabel: "Book a check-in", ctaIcon: <CalendarPlus size={14} />,
        },
        final_aligner: {
            kicker: "Almost there",
            title: "🎯 Final aligner — last stretch!",
            sub: "Give this last tray its full 22 hours every day. Once it's done your doctor will switch you to retention.",
            pills: [
                { text: `Aligner ${stage}/${total}` },
                ...(progress?.stage?.next_switch_in_days != null ? [{ text: `Wraps up in ${Math.max(0, progress.stage.next_switch_in_days)}d` }] : []),
                ...(startedAt ? [{ text: `Started ${fmtDate(startedAt, { month: "short", year: "numeric" })}` }] : []),
            ],
            ctaLabel: "Book retention review", ctaIcon: <CalendarPlus size={14} />,
        },
        complete: {
            kicker: "Current phase",
            title: "🦷 Retention Phase",
            sub: "Active treatment is complete. Keep wearing your retainer to lock in your beautiful results.",
            pills: [
                { text: `${total}/${total} aligners done` },
                ...(startedAt ? [{ text: `Treatment: ${fmtDate(startedAt, { month: "short", year: "numeric" })} – ${fmtDate(activeCase?.updated_at, { month: "short", year: "numeric" })}` }] : []),
                { text: "🟢 On Track" },
                { text: "Recall in 6 months" },
            ],
            ctaLabel: "Book retention review", ctaIcon: <CalendarPlus size={14} />,
        },
        pre_treatment: {
            kicker: "Coming up",
            title: "🦷 Aligners on the way",
            sub: "Your case is in production. The treatment dashboard will unlock the day your doctor fits you in-chair.",
            pills: [{ text: "In production" }, { text: "Awaiting fitting" }],
            ctaLabel: "Track case status", ctaIcon: <ArrowRight size={14} />,
        },
        no_case: {
            kicker: "Getting started",
            title: "Welcome to your smile journey",
            sub: "Your doctor will open a case for you after your consultation.",
            pills: [{ text: "Awaiting consultation" }],
            ctaLabel: "View appointments", ctaIcon: <CalendarPlus size={14} />,
        },

        // ── Refinement variants ──────────────────────────────────────
        refinement_pre_treatment: {
            kicker: `Refinement${cycleSuffix} · Coming up`,
            title: "🔄 Refinement aligners on the way",
            sub: "Your refinement trays are being made. Treatment unlocks the day your doctor fits this new set in-chair.",
            pills: [
                { text: `Refinement${cycleSuffix}` },
                { text: "In production" },
            ],
            ctaLabel: "Track case status", ctaIcon: <ArrowRight size={14} />,
        },
        refinement_in_treatment: {
            kicker: `Refinement${cycleSuffix} · In progress`,
            title: `🔄 Refinement cycle — Aligner ${stage} of ${total || "?"}`,
            sub: "Same routine — 22 hours of wear a day, weekly check-ins. Your doctor will review your progress at each switch.",
            pills: [
                { text: `${stage}/${total || "?"} aligners` },
                ...(progress?.stage?.next_switch_in_days != null ? [{ text: `Next switch in ${progress.stage.next_switch_in_days}d` }] : []),
                ...(startedAt ? [{ text: `Refinement started ${fmtDate(startedAt, { month: "short", year: "numeric" })}` }] : []),
                { text: "🟢 On Track" },
            ],
            ctaLabel: "Book a check-in", ctaIcon: <CalendarPlus size={14} />,
        },
        refinement_final_aligner: {
            kicker: `Refinement${cycleSuffix} · Almost done`,
            title: "🎯 Refinement nearly complete — last tray!",
            sub: "Give this final refinement tray its full 22 hours every day. Once done, you're back to retention.",
            pills: [
                { text: `Aligner ${stage}/${total}` },
                ...(progress?.stage?.next_switch_in_days != null ? [{ text: `Wraps up in ${Math.max(0, progress.stage.next_switch_in_days)}d` }] : []),
            ],
            ctaLabel: "Book retention review", ctaIcon: <CalendarPlus size={14} />,
        },
        refinement_complete: {
            kicker: `Refinement${cycleSuffix} · Wrapped`,
            title: "✅ Refinement complete — back to retention",
            sub: "Your refinement cycle is done. Keep wearing your retainer to lock in the result.",
            pills: [
                { text: `${total}/${total} aligners done` },
                { text: "🟢 On Track" },
                { text: "Recall in 6 months" },
            ],
            ctaLabel: "Book retention review", ctaIcon: <CalendarPlus size={14} />,
        },

        // ── Retainer (child case) variants ───────────────────────────
        retainer_in_production: {
            kicker: "Custom retainer · Coming up",
            title: "🦷 Your new retainer is being made",
            sub: "Once it's ready, your doctor will fit it in-chair. You'll get notified the moment that happens.",
            pills: [
                { text: "Retainer" },
                { text: "In production" },
            ],
            ctaLabel: "Track case status", ctaIcon: <ArrowRight size={14} />,
        },
        retainer_active: {
            kicker: "Custom retainer · Active",
            title: "🦷 Wear your retainer as prescribed",
            sub: "Your custom retainer is fitted. Follow the wear schedule your doctor set to keep your teeth in place.",
            pills: [
                { text: "Retainer" },
                ...(startedAt ? [{ text: `Fitted ${fmtDate(startedAt, { month: "short", year: "numeric" })}` }] : []),
                { text: "🟢 On Track" },
            ],
            ctaLabel: "Book a check-in", ctaIcon: <CalendarPlus size={14} />,
        },
    };
    const v = variants[phase] || variants.in_treatment;
    const ctaTo = activeCase?.doctor_id ? `/book/${activeCase.doctor_id}` : "/patient/appointments";

    // Refinement banner gets a warmer (amber-tinted) gradient so it's
    // visually distinct from the primary teal banner.
    const isRefinement = kind === "refinement";
    const gradient = isRefinement
        ? "linear-gradient(135deg, hsl(35 80% 22%) 0%, hsl(28 86% 36%) 55%, hsl(40 90% 48%) 100%)"
        : "linear-gradient(135deg, hsl(193 100% 22%) 0%, hsl(192 100% 31%) 55%, hsl(173 58% 42%) 100%)";

    return (
        <div
            className="relative overflow-hidden rounded-2xl p-6 sm:p-7 mb-3 text-white shadow-sm"
            style={{ background: gradient }}
            data-testid={`phase-banner-${phase}`}
        >
            <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-white/[0.06]" />
            <div className="absolute bottom-[-50px] right-20 w-32 h-32 rounded-full bg-white/[0.04]" />

            <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-white/60 mb-2">{v.kicker}</div>
                <h1 className="font-display text-xl sm:text-2xl font-bold leading-tight">{v.title}</h1>
                <p className="text-sm text-white/80 mt-1.5 max-w-2xl">{v.sub}</p>

                <div className="flex flex-wrap items-center gap-2 mt-4">
                    {v.pills.map((p, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 text-xs font-medium border border-white/15">
                            {p.text}
                        </span>
                    ))}
                </div>

                <Link to={ctaTo}>
                    <Button className="mt-5 rounded-lg bg-white/15 hover:bg-white/25 text-white border border-white/25 backdrop-blur-sm h-10 px-4 font-medium gap-1.5">
                        {v.ctaIcon} {v.ctaLabel}
                    </Button>
                </Link>
            </div>

            {/* small caption under banner with doctor name + photo */}
            {careTeam?.doctor?.name && (
                <div className="relative mt-4 flex items-center gap-2 text-[11px] text-white/70">
                    <DoctorAvatar
                        src={careTeam.doctor.image}
                        name={careTeam.doctor.name}
                        size={22}
                        initialsClassName="bg-white/15 border border-white/25 text-white text-[9px]"
                    />
                    <span>
                        With {careTeam.doctor.name}{careTeam.doctor.clinic_name ? ` · ${careTeam.doctor.clinic_name}` : ""}
                    </span>
                </div>
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════
// TREATMENT TIMELINE — 4 phases (Scan → Treatment → Retention → Complete)
// ════════════════════════════════════════════════════════════════════
function TreatmentTimeline({ activeCase, progress, cases }) {
    const phase = derivePhase(activeCase);
    const kind = caseKindOf(activeCase);
    const total = Math.max(
        Number(activeCase?.plan?.aligner_count_upper || 0),
        Number(activeCase?.plan?.aligner_count_lower || 0),
    );
    const stage = Number(activeCase?.current_aligner_stage || 0);
    const cycleN = cycleNumber(cases, activeCase);

    // Map UI phase → 0..3 active index. Refinement / retainer variants
    // collapse onto the same 4-step backbone so the visual model stays
    // simple, but the cycle chip in the corner tells the patient what
    // cycle they're in.
    const activeIdx = {
        no_case: 0, pre_treatment: 0, in_treatment: 1, final_aligner: 1, complete: 2,
        refinement_pre_treatment: 0,
        refinement_in_treatment: 1,
        refinement_final_aligner: 1,
        refinement_complete: 2,
        retainer_in_production: 0,
        retainer_active: 2,
    }[phase] ?? 0;

    const phases = [
        {
            key: "scan",
            label: "Scan",
            date: activeCase?.created_at ? fmtDate(activeCase.created_at, { month: "short", year: "numeric" }) : "—",
            badge: activeIdx > 0 ? "Done" : null,
        },
        {
            key: "treatment",
            label: kind === "refinement" ? "Refinement" : (kind === "retainer" ? "Retainer" : "Treatment"),
            date: activeCase?.treatment_activated_at ? fmtDate(activeCase.treatment_activated_at, { month: "short", year: "numeric" }) : "—",
            badge: total > 0
                ? (activeIdx > 1 ? `${total}/${total}` : `${stage}/${total}`)
                : null,
        },
        {
            key: "retention",
            label: "Retention",
            date: activeIdx >= 2 ? "Now" : "Up next",
            badge: activeIdx === 2 ? "Here" : (activeIdx > 2 ? "Done" : null),
        },
        {
            key: "complete",
            label: "Complete",
            date: activeIdx >= 3 ? "Done" : "~ Future",
            badge: null,
        },
    ];

    // Line fill 0..1
    const lineFill = (i) => {
        if (i < activeIdx) return 1;
        if (i > activeIdx) return 0;
        // current phase — partial intra-phase fill
        if ((phase === "in_treatment" || phase === "refinement_in_treatment") && total > 0) return Math.min(1, stage / total);
        if (phase === "final_aligner" || phase === "refinement_final_aligner") return 0.95;
        if (phase === "pre_treatment" || phase === "refinement_pre_treatment" || phase === "retainer_in_production") return 0.4;
        return 0.5;
    };

    // Cycle marker shown in the corner of the timeline card for non-primary cases.
    const cycleChip = kind === "primary"
        ? null
        : (kind === "refinement"
            ? `Refinement cycle${cycleN > 0 ? ` #${cycleN}` : ""}`
            : `Custom retainer${cycleN > 0 ? ` #${cycleN}` : ""}`);

    return (
        <div className="bg-card border border-border rounded-2xl p-5 mb-3" data-testid="treatment-timeline">
            <div className="flex items-center justify-between mb-3">
                <SectionHeader title="Treatment timeline" />
                {cycleChip && (
                    <span
                        className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ring-1 ${
                            kind === "refinement"
                                ? "bg-amber-50 text-amber-800 ring-amber-200"
                                : "bg-indigo-50 text-indigo-700 ring-indigo-200"
                        }`}
                        data-testid="timeline-cycle-chip"
                    >
                        {cycleChip}
                    </span>
                )}
            </div>

            <div className="px-2 sm:px-4 pt-1">
                <div className="flex items-start">
                    {phases.map((p, i) => {
                        const done = i < activeIdx;
                        const active = i === activeIdx;
                        return (
                            <React.Fragment key={p.key}>
                                <div className="flex flex-col items-center" style={{ width: i === 0 || i === phases.length - 1 ? 70 : 80, flexShrink: 0 }}>
                                    {/* Dot — note the line is drawn between dots not into this column */}
                                    {done ? (
                                        <div className="w-4 h-4 rounded-full bg-primary border-2 border-primary grid place-items-center text-white">
                                            <span className="text-[8px] leading-none">✓</span>
                                        </div>
                                    ) : active ? (
                                        <div className="w-4 h-4 rounded-full bg-card border-2 border-primary" style={{ boxShadow: "0 0 0 4px hsla(192,100%,31%,0.15)" }} />
                                    ) : (
                                        <div className="w-4 h-4 rounded-full bg-card border-2 border-border" />
                                    )}
                                    <div className={`text-xs mt-2 ${done ? "text-primary font-medium" : active ? "font-semibold" : "text-muted-foreground"} text-center`}>
                                        {p.label}
                                    </div>
                                    <div className={`text-[10px] text-center mt-0.5 ${active ? "text-primary font-medium" : "text-muted-foreground"}`}>
                                        {p.date}
                                    </div>
                                    {p.badge && (
                                        <span className={`text-[9px] font-semibold mt-1 px-2 py-0.5 rounded-full ${
                                            active ? "bg-primary text-white" : "bg-primary/10 text-primary"
                                        }`}>
                                            {p.badge}
                                        </span>
                                    )}
                                </div>
                                {i < phases.length - 1 && (
                                    <div className="flex-1 h-0.5 bg-border relative mt-2">
                                        <div className="h-full bg-primary transition-all" style={{ width: `${lineFill(i) * 100}%` }} />
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════
// STATS ROW — 4 mini tiles
// ════════════════════════════════════════════════════════════════════
function StatsRow({ activeCase, progress, checkIns, wearLog }) {
    const total = Math.max(
        Number(activeCase?.plan?.aligner_count_upper || 0),
        Number(activeCase?.plan?.aligner_count_lower || 0),
    );
    const stage = Number(activeCase?.current_aligner_stage || 0);

    const [nowMs] = useState(() => Date.now());
    const weeks = useMemo(() => {
        if (!activeCase?.treatment_activated_at) return 0;
        return Math.max(1, Math.floor((nowMs - new Date(activeCase.treatment_activated_at).getTime()) / (7 * 86400000)));
    }, [activeCase?.treatment_activated_at, nowMs]);

    const checkInCount = checkIns?.check_ins?.length || 0;
    const avgWear = progress?.wear?.avg_60d != null ? Number(progress.wear.avg_60d).toFixed(1)
        : progress?.wear?.avg_7d != null ? Number(progress.wear.avg_7d).toFixed(1)
        : null;

    const pct = total > 0 ? Math.round((stage / total) * 100) : 0;

    const tiles = [
        {
            label: "Aligners completed",
            value: total > 0 ? `${stage}/${total}` : "—",
            sub: total > 0 ? (pct === 100 ? "100% done 🎉" : `${pct}% through plan`) : "Plan pending",
        },
        {
            label: "Weeks of treatment",
            value: weeks || "—",
            sub: activeCase?.treatment_activated_at ? `Since ${fmtDate(activeCase.treatment_activated_at, { month: "short", year: "numeric" })}` : "",
        },
        {
            label: "Check-ins submitted",
            value: checkInCount,
            sub: checkInCount > 0 ? `${checkInCount} smile photo${checkInCount === 1 ? "" : "s"}` : "Upload to start",
        },
        {
            label: "Avg daily wear (hrs)",
            value: avgWear ?? "—",
            sub: `Target: ${progress?.wear?.target_hours ?? 22} hrs`,
        },
    ];

    return (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-3" data-testid="journey-stats-row">
            {tiles.map((t, i) => (
                <div key={i} className="bg-card border border-border rounded-xl p-3.5">
                    <div className="font-display font-bold text-[22px] leading-none">{t.value}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">{t.label}</div>
                    {t.sub && <div className="text-[11px] text-primary mt-1 font-medium">{t.sub}</div>}
                </div>
            ))}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════
// TAB BAR
// ════════════════════════════════════════════════════════════════════
function TabBar({ tab, setTab }) {
    const tabs = [
        { id: "aligners",  label: "Aligners",       Icon: Tooth },
        { id: "retention", label: "Retention",      Icon: ShieldCheck },
        { id: "photos",    label: "Photo Timeline", Icon: Camera },
        { id: "wear",      label: "Wear Log",       Icon: Clock },
        { id: "notes",     label: "Clinical Alerts", Icon: Notebook },
    ];
    return (
        <div className="flex border-b border-border overflow-x-auto -mx-1 px-1" data-testid="mycase-tablist">
            {tabs.map(({ id, label, Icon }) => {
                const active = tab === id;
                return (
                    <button
                        key={id}
                        onClick={() => setTab(id)}
                        className={`px-4 py-2.5 inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                        data-testid={`mycase-tab-${id}`}
                    >
                        <Icon size={15} weight={active ? "fill" : "regular"} />
                        {label}
                    </button>
                );
            })}
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────
// Shared — section header (matches Dashboard's)
// ────────────────────────────────────────────────────────────────────
function SectionHeader({ title, right }) {
    return (
        <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
                <div className="w-[3px] h-4 bg-primary rounded-full" />
                <h3 className="text-sm font-medium">{title}</h3>
            </div>
            {right}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════
// TAB: Aligners — list of all aligners with status
// ════════════════════════════════════════════════════════════════════
function AlignersTab({ activeCase, progress }) {
    const [showAll, setShowAll] = useState(false);
    const list = progress?.schedule?.per_aligner || [];
    const total = list.length;
    const stage = Number(activeCase?.current_aligner_stage || 0);
    const pct = total > 0 ? Math.round((stage / total) * 100) : 0;
    const completedAll = stage >= total && total > 0;

    const visible = showAll ? list : list.slice(0, 4);

    if (total === 0) {
        return (
            <div className="bg-card border border-border rounded-2xl p-6 text-center text-sm text-muted-foreground">
                Your aligner schedule will appear once the planner confirms your plan.
            </div>
        );
    }

    return (
        <div className="bg-card border border-border rounded-2xl p-5 mt-3" data-testid="aligners-tab">
            <SectionHeader
                title={`Active treatment — ${stage} / ${total} ${completedAll ? "complete" : ""}`.trim()}
                right={completedAll ? <span className="text-xs text-primary font-medium">100% ✅</span> : <span className="text-xs text-muted-foreground">{pct}%</span>}
            />

            <div className="mb-4">
                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                </div>
                <div className="flex items-center justify-between mt-1.5 text-[11px]">
                    <span className="text-muted-foreground">
                        {activeCase?.treatment_activated_at ? `Started ${fmtShortDate(activeCase.treatment_activated_at)}` : ""}
                    </span>
                    <span className="text-primary font-medium">
                        {completedAll ? `Completed ${fmtShortDate(activeCase?.updated_at)}` : `Aligner ${stage} active`}
                    </span>
                </div>
            </div>

            <div className="border-t border-border">
                {visible.map((a) => {
                    const isCurrent = a.status === "current";
                    const isDone = a.status === "completed";
                    return (
                        <div key={a.index} className="flex items-center gap-3 py-2.5 border-b border-border last:border-0" data-testid={`aligner-row-${a.index}`}>
                            <div className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-semibold shrink-0 ${
                                isCurrent ? "bg-primary text-white"
                                : isDone ? "bg-primary/10 text-primary"
                                : "bg-secondary text-muted-foreground"
                            }`}>
                                {a.index}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium">
                                    Aligner {a.index}
                                    {a.index === total && <span className="text-muted-foreground font-normal"> (Final)</span>}
                                    {a.is_overridden && <span className="ml-1.5 inline-block text-[9px] font-bold uppercase tracking-wider bg-primary/15 text-primary px-1.5 py-0.5 rounded-full align-middle">Custom</span>}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                    {a.start_date && a.end_date
                                        ? `${fmtShortDate(a.start_date)} → ${fmtShortDate(a.end_date)}`
                                        : `${a.days} days planned`}
                                </div>
                            </div>
                            <span className={`text-[10px] font-semibold px-2.5 py-0.5 rounded-full ${
                                isCurrent ? "bg-primary text-white"
                                : isDone ? "bg-primary/10 text-primary"
                                : "bg-secondary text-muted-foreground"
                            }`}>
                                {isCurrent ? "In progress" : isDone ? "Completed" : "Upcoming"}
                            </span>
                        </div>
                    );
                })}
            </div>

            {total > 4 && (
                <div className="text-center pt-3">
                    <button
                        onClick={() => setShowAll(!showAll)}
                        className="text-xs text-primary inline-flex items-center gap-1 hover:underline"
                        data-testid="aligners-toggle"
                    >
                        {showAll ? <CaretUp size={13} /> : <CaretDown size={13} />}
                        {showAll ? "Show less" : `Show all ${total} aligners`}
                    </button>
                </div>
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════
// TAB: Retention
// ════════════════════════════════════════════════════════════════════
function RetentionTab({ activeCase, progress, careTeam }) {
    const phase = derivePhase(activeCase);
    const isActive = phase === "complete";

    if (!isActive) {
        return (
            <div className="bg-card border border-border rounded-2xl p-6 mt-3 text-center" data-testid="retention-tab-pending">
                <ShieldCheck size={36} weight="duotone" className="mx-auto text-primary/40 mb-3" />
                <h3 className="font-display text-lg font-semibold mb-1.5">Retention is up next</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    Once your final aligner is done, your doctor will set up your retention plan here — retainer type, wear schedule, and recall dates.
                </p>
            </div>
        );
    }

    const doctorName = careTeam?.doctor?.name || activeCase?.doctor_name || "your doctor";

    return (
        <div className="mt-3" data-testid="retention-tab">
            {/* Plan card — purple-accented like the mockup, but using
                our primary teal palette */}
            <div className="bg-primary/[0.06] border border-primary/30 rounded-2xl p-5 mb-3">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-primary grid place-items-center text-white shrink-0">
                        <ShieldCheck size={18} weight="fill" />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-primary">Retention plan — Active</div>
                        <div className="text-xs text-primary/70">Set by {doctorName}</div>
                    </div>
                </div>
                {[
                    ["Retainer type", "Clear (Vivera-style)"],
                    ["Phase 1 — Duration", "Full time · 3 months"],
                    ["Phase 2 — Duration", "Nights only · ongoing"],
                    ["Follow-up recall", "6 months"],
                    ["Status", "🟢 On Track"],
                ].map(([k, v], i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-primary/15 last:border-0 text-sm">
                        <span className="text-xs text-primary/70">{k}</span>
                        <span className="font-medium text-primary">{v}</span>
                    </div>
                ))}
            </div>

            {/* Streak card */}
            <div className="bg-card border border-border rounded-2xl p-5 mb-3">
                <SectionHeader title="Retainer wear streak" />
                <div className="flex items-center gap-4 mb-3">
                    <div className="text-4xl">🔥</div>
                    <div>
                        <div className="font-display font-bold text-[26px] leading-none">— nights</div>
                        <div className="text-xs text-muted-foreground mt-1">Log your retainer wear to start a streak.</div>
                    </div>
                </div>
            </div>

            {/* Roadmap */}
            <div className="bg-card border border-border rounded-2xl p-5">
                <SectionHeader title="Retention roadmap" />
                <div>
                    {[
                        { title: "Phase 1 — Full time wear", sub: "First 3 months · 22 h/day", active: true },
                        { title: "Phase 2 — Nights only", sub: "Ongoing · 8 h/night", active: false },
                        { title: "Recall appointment", sub: "~ 6 months post-treatment", active: false, cta: true },
                    ].map((row, i, arr) => (
                        <div key={i} className="flex gap-3 py-3 border-b border-border last:border-0">
                            <div className="flex flex-col items-center w-5 shrink-0 pt-1">
                                <div className={`w-3 h-3 rounded-full ${row.active ? "bg-primary" : "bg-border"}`} />
                                {i < arr.length - 1 && (
                                    <div className={`flex-1 w-0.5 mt-1 min-h-[24px] ${row.active ? "bg-primary/40" : "bg-border"}`} />
                                )}
                            </div>
                            <div className="flex-1">
                                <div className={`text-sm font-medium ${row.active ? "" : "text-muted-foreground"}`}>{row.title}</div>
                                <div className="text-xs text-muted-foreground mt-0.5">{row.sub}</div>
                                {row.active && <div className="text-[11px] text-primary mt-1 font-medium">← Currently here</div>}
                                {row.cta && (
                                    <Link to={activeCase?.doctor_id ? `/book/${activeCase.doctor_id}` : "/patient/appointments"}>
                                        <Button variant="outline" size="sm" className="mt-2 rounded-lg gap-1.5 text-xs h-8">
                                            <CalendarPlus size={13} /> Book recall
                                        </Button>
                                    </Link>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════
// TAB: Photo Timeline — uses the unified /patient/me/case/{id}/photos
// ════════════════════════════════════════════════════════════════════
function PhotoTimelineTab({ activeCase }) {
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewer, setViewer] = useState(null);

    useEffect(() => {
        if (!activeCase?.id) return;
        api.get(`/patient/me/case/${activeCase.id}/photos`)
            .then((r) => setPhotos(r.data || []))
            .catch(() => setPhotos([]))
            .finally(() => setLoading(false));
    }, [activeCase?.id]);

    // Group photos by ISO week so the strip shows weekly cells
    const grouped = useMemo(() => {
        const byWeek = {};
        for (const p of photos) {
            const d = new Date(p.uploaded_at);
            const weekKey = `${d.getFullYear()}-W${Math.ceil(((d - new Date(d.getFullYear(), 0, 1)) / 86400000 + new Date(d.getFullYear(), 0, 1).getDay() + 1) / 7)}`;
            if (!byWeek[weekKey]) byWeek[weekKey] = { weekKey, date: d, photos: [] };
            byWeek[weekKey].photos.push(p);
            if (d > byWeek[weekKey].date) byWeek[weekKey].date = d;
        }
        return Object.values(byWeek).sort((a, b) => a.date - b.date);
    }, [photos]);

    return (
        <div className="mt-3" data-testid="photos-tab">
            <div className="bg-card border border-border rounded-2xl p-5 mb-3">
                <SectionHeader
                    title="Weekly smile photos"
                    right={(
                        <Link to="/patient" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                            <Plus size={13} /> Upload
                        </Link>
                    )}
                />
                <p className="text-xs text-muted-foreground mb-3">
                    Tap any photo to view it full-size. Your doctor reviews these after each Sunday check-in.
                </p>

                {loading && <div className="text-center text-sm text-muted-foreground py-6">Loading photos…</div>}

                {!loading && grouped.length === 0 && (
                    <div className="text-center py-8 text-sm text-muted-foreground">
                        <Camera size={28} weight="duotone" className="mx-auto text-primary/40 mb-2" />
                        No smile photos yet. Submit your first weekly check-in from the dashboard to start your photo timeline.
                    </div>
                )}

                {!loading && grouped.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                        {grouped.map((week, idx) => {
                            const isLatest = idx === grouped.length - 1;
                            const top = week.photos[0];
                            return (
                                <button
                                    key={week.weekKey}
                                    onClick={() => setViewer(top)}
                                    className={`shrink-0 w-20 flex flex-col gap-1 group ${isLatest ? "ring-2 ring-primary/30 rounded-md p-1 -m-1" : ""}`}
                                    data-testid={`photo-week-${week.weekKey}`}
                                >
                                    <div className={`w-20 h-20 rounded-md overflow-hidden border ${isLatest ? "border-primary" : "border-border"} relative`}>
                                        <img src={top.data_url} alt={`Week ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                        {isLatest && (
                                            <div className="absolute bottom-0 inset-x-0 bg-primary/90 text-white text-[9px] font-bold text-center py-0.5">
                                                Latest
                                            </div>
                                        )}
                                        {week.photos.length > 1 && (
                                            <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded-full bg-black/60 text-white text-[8px] font-bold">
                                                {week.photos.length}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-[10px] font-medium text-center">{week.weekKey.split("-")[1]}</div>
                                    <div className="text-[9px] text-muted-foreground text-center">{fmtShortDate(week.date.toISOString())}</div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Compare hint card */}
            {grouped.length >= 2 && (
                <div className="bg-primary/[0.06] border border-primary/40 rounded-2xl p-4 flex items-center gap-3" data-testid="photo-compare-hint">
                    <Sparkle size={20} weight="fill" className="text-primary shrink-0" />
                    <div className="flex-1">
                        <div className="text-sm font-medium text-primary">See your progress</div>
                        <div className="text-xs text-primary/75 mt-0.5">
                            Compare your first and most recent smile photos side-by-side to see how far you&apos;ve come!
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        className="rounded-lg gap-1.5 text-xs h-8 shrink-0 border-primary/40 text-primary hover:bg-primary/10"
                        onClick={() => setViewer({ compare: true, before: grouped[0]?.photos?.[0], after: grouped[grouped.length - 1]?.photos?.[0] })}
                        data-testid="photo-compare-btn"
                    >
                        Compare
                    </Button>
                </div>
            )}

            {/* Full-screen viewer */}
            {viewer && (
                <div className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4" onClick={() => setViewer(null)}>
                    <button
                        onClick={() => setViewer(null)}
                        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"
                    ><XCircle size={20} weight="fill" /></button>
                    <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
                        {viewer.compare ? (
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div>
                                    <div className="text-white/80 text-xs uppercase tracking-wider mb-2 text-center">Before · {fmtShortDate(viewer.before?.uploaded_at)}</div>
                                    {viewer.before && <img src={viewer.before.data_url} alt="Before" className="w-full rounded-xl" />}
                                </div>
                                <div>
                                    <div className="text-white/80 text-xs uppercase tracking-wider mb-2 text-center">After · {fmtShortDate(viewer.after?.uploaded_at)}</div>
                                    {viewer.after && <img src={viewer.after.data_url} alt="After" className="w-full rounded-xl" />}
                                </div>
                            </div>
                        ) : (
                            <>
                                <img src={viewer.data_url} alt={viewer.note || "Smile photo"} className="max-w-full max-h-[80vh] object-contain rounded-xl mx-auto" />
                                {viewer.note && <div className="mt-3 text-white/90 text-center text-sm">{viewer.note}</div>}
                                <div className="mt-1 text-white/60 text-center text-xs">{new Date(viewer.uploaded_at).toLocaleString("en-IN")}</div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════
// TAB: Wear Log
// ════════════════════════════════════════════════════════════════════
function WearLogTab({ wearLog, progress, onReload }) {
    const [dialogOpen, setDialogOpen] = useState(false);

    // Compose a 7-day window (newest last) from the wear-log list +
    // progress.wear.series (which has fully-rendered last-30 days).
    // iter98.99dy — we layer provenance from `wearLog.logs` onto each day
    // so the row can show a small "Logged by Rachel · phone call" badge
    // whenever a coach entered the value on the patient's behalf.
    const provenanceByDate = useMemo(() => {
        const m = new Map();
        for (const l of (wearLog?.logs || [])) {
            m.set(l.log_date, {
                logged_by_role: l.logged_by_role || null,
                logged_by_name: l.logged_by_name || null,
                verification_method: l.verification_method || null,
            });
        }
        return m;
    }, [wearLog]);

    const lastWeek = useMemo(() => {
        const series = (progress?.wear?.series || []).slice(-7);
        if (series.length === 7) return series.map((s) => ({
            date: s.date,
            label: new Date(s.date).toLocaleDateString("en-IN", { weekday: "short" }),
            hours: s.hours || 0,
            pct: Math.min(100, Math.round(((s.hours || 0) / (progress?.wear?.target_hours || 22)) * 100)),
            logged: s.logged,
            ...(provenanceByDate.get(s.date) || {}),
        }));
        // Fallback: build from wearLog.logs
        const map = new Map((wearLog?.logs || []).map((l) => [l.log_date, l.hours]));
        const out = [];
        for (let i = 6; i >= 0; i--) {
            const d = new Date(Date.now() - i * 86400000);
            const key = d.toISOString().slice(0, 10);
            const h = map.get(key) || 0;
            out.push({
                date: key,
                label: d.toLocaleDateString("en-IN", { weekday: "short" }),
                hours: h,
                pct: Math.min(100, Math.round((h / 22) * 100)),
                logged: !!map.get(key),
                ...(provenanceByDate.get(key) || {}),
            });
        }
        return out;
    }, [progress, wearLog, provenanceByDate]);

    const target = progress?.wear?.target_hours || 22;
    const avg = lastWeek.reduce((a, b) => a + b.hours, 0) / (lastWeek.filter((d) => d.logged).length || 1);

    return (
        <div className="bg-card border border-border rounded-2xl p-5 mt-3" data-testid="wear-tab">
            <SectionHeader
                title="7-day wear log"
                right={(
                    <Button variant="outline" size="sm" className="rounded-lg gap-1.5 text-xs h-8" onClick={() => setDialogOpen(true)} data-testid="wear-tab-log-btn">
                        <ClockUser size={14} /> Log today
                    </Button>
                )}
            />

            {/* Chart */}
            <div className="flex items-end gap-1 h-20 mb-2">
                {lastWeek.map((d, i) => {
                    const cls = !d.logged
                        ? "bg-muted/60"
                        : d.pct >= 90 ? "bg-primary"
                            : d.pct >= 65 ? "bg-amber-500"
                                : "bg-rose-500";
                    return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <div className="text-[9px] font-medium text-muted-foreground">{d.logged ? d.hours.toFixed(1) : "—"}</div>
                            <div className="w-full bg-secondary/60 h-16 rounded-t-[3px] flex items-end overflow-hidden">
                                <div className={`w-full transition-all ${cls}`} style={{ height: `${Math.max(d.pct, 4)}%` }} />
                            </div>
                            <div className="text-[9px] text-muted-foreground">{d.label}</div>
                        </div>
                    );
                })}
            </div>

            <div className="flex items-center justify-between mb-2 text-xs">
                <div>
                    <span className="text-muted-foreground">Weekly average: </span>
                    <span className="font-semibold text-primary">{avg.toFixed(1)} hrs / day</span>
                </div>
                <span className="text-[11px] text-muted-foreground">Target: {target} hrs / day</span>
            </div>

            <div className="border-t border-border mt-3 pt-1">
                {lastWeek.slice().reverse().map((d, i) => {
                    const ok = d.pct >= 90;
                    const warn = !ok && d.pct >= 65;
                    const isCoach = d.logged && d.logged_by_role === "coach";
                    return (
                        <div key={i} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                            <div className="text-xs font-medium w-9 text-muted-foreground">{d.label}</div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-medium">{d.logged ? `${d.hours.toFixed(1)} hrs` : "Not logged"}</span>
                                    {/* iter98.99dy — provenance badge whenever a PES entered this day on
                                        the patient's behalf (after a phone call etc). Lets the patient
                                        see who logged what and correct if needed via "Log today". */}
                                    {isCoach && (
                                        <span
                                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700"
                                            title={`Logged by ${d.logged_by_name || "your engagement specialist"}${d.verification_method ? ` · ${d.verification_method.replace(/_/g, " ")}` : ""}`}
                                            data-testid={`patient-wear-coach-badge-${d.date}`}
                                        >
                                            <ClockUser size={9} weight="fill" />
                                            Logged by {d.logged_by_name?.split(/\s+/)[0] || "team"}
                                        </span>
                                    )}
                                    <span className="text-[11px] text-muted-foreground ml-auto">{d.logged ? `${d.pct}% of target` : ""}</span>
                                </div>
                                <div className="h-1 rounded-full bg-secondary overflow-hidden">
                                    <div className={`h-full transition-all ${ok ? "bg-primary" : warn ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${d.pct}%` }} />
                                </div>
                            </div>
                            {d.logged ? (
                                ok ? <CheckCircle size={16} weight="fill" className="text-primary shrink-0" />
                                    : warn ? <Warning size={16} weight="fill" className="text-amber-500 shrink-0" />
                                        : <XCircle size={16} weight="fill" className="text-rose-500 shrink-0" />
                            ) : (
                                <div className="w-4 h-4 rounded-full border-[1.5px] border-border shrink-0" />
                            )}
                        </div>
                    );
                })}
            </div>

            <WearLogQuickDialog open={dialogOpen} onClose={() => setDialogOpen(false)} target={target} onSaved={() => { setDialogOpen(false); onReload?.(); }} />
        </div>
    );
}

function WearLogQuickDialog({ open, onClose, target, onSaved }) {
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <WearLogQuickBody key={open ? "open" : "closed"} target={target} onClose={onClose} onSaved={onSaved} />
        </Dialog>
    );
}

function WearLogQuickBody({ target, onClose, onSaved }) {
    const [hours, setHours] = useState("20");
    const [mins, setMins] = useState("0");
    const [busy, setBusy] = useState(false);
    const h = Math.max(0, Math.min(24, Number(hours) || 0));
    const m = Math.max(0, Math.min(59, Number(mins) || 0));
    const total = h + m / 60;
    const preview = total >= target
        ? `You hit the ${target}hr target — nice work.`
        : `${(target - total).toFixed(1)}h short of the ${target}hr target.`;

    const submit = async () => {
        setBusy(true);
        try {
            await api.post("/patient/me/wear-log", { hours: Number(total.toFixed(2)) });
            toast.success("Wear time saved");
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't save");
        } finally { setBusy(false); }
    };

    return (
        <DialogContent className="max-w-sm">
            <DialogHeader>
                <DialogTitle>Log today&apos;s wear</DialogTitle>
                <DialogDescription>How long did you wear your aligners today?</DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-3 mt-2">
                <div className="flex-1">
                    <label className="text-xs text-muted-foreground block mb-1.5">Hours</label>
                    <Input type="number" min="0" max="24" value={hours} onChange={(e) => setHours(e.target.value)} className="text-center text-xl font-semibold h-12" />
                </div>
                <div className="text-xl text-muted-foreground pt-5">:</div>
                <div className="flex-1">
                    <label className="text-xs text-muted-foreground block mb-1.5">Minutes</label>
                    <Input type="number" min="0" max="59" value={mins} onChange={(e) => setMins(e.target.value)} className="text-center text-xl font-semibold h-12" />
                </div>
            </div>
            <div className="text-xs text-muted-foreground mt-1">{preview}</div>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
            </DialogFooter>
        </DialogContent>
    );
}

// ════════════════════════════════════════════════════════════════════
// TAB: Doctor Notes
// ════════════════════════════════════════════════════════════════════
function DoctorNotesTab({ progress, careTeam }) {
    const notes = progress?.nudges || [];
    const doctorName = careTeam?.doctor?.name || "your doctor";
    const initials = doctorName.replace(/^Dr\.?\s*/i, "").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "DR";

    return (
        <div className="bg-card border border-border rounded-2xl p-5 mt-3" data-testid="notes-tab">
            <SectionHeader
                title={`Clinical alerts from ${doctorName.startsWith("Dr") ? doctorName : `Dr. ${doctorName}`}`}
                right={(
                    <Link to="/patient/clinic-chat" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                        <ChatCircleDots size={13} /> Message
                    </Link>
                )}
            />

            {notes.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                    <Notebook size={28} weight="duotone" className="mx-auto text-primary/40 mb-2" />
                    Your doctor hasn&apos;t sent any clinical alerts yet. These are automated reminders and updates — they&apos;ll appear here after each weekly review.
                </div>
            ) : (
                <div>
                    {notes.map((n) => (
                        <div key={n.id} className="flex gap-3 py-3 border-b border-border last:border-0" data-testid={`doctor-note-${n.id}`}>
                            <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/30 grid place-items-center text-primary text-xs font-bold shrink-0">
                                {initials}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                    <span className="text-xs font-medium">{doctorName.startsWith("Dr") ? doctorName : `Dr. ${doctorName}`}</span>
                                    <span className="text-[11px] text-muted-foreground">{fmtRelative(n.sent_at)} · {fmtShortDate(n.sent_at)}</span>
                                    {n.reason && (
                                        <span className="text-[9px] font-bold uppercase tracking-wider bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                            {n.reason.replace(/_/g, " ")}
                                        </span>
                                    )}
                                </div>
                                <div className="text-sm leading-relaxed text-foreground/85 bg-secondary/50 border-l-[3px] border-primary/60 rounded-lg p-3">
                                    {n.message}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
